import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { errorToast, infoToast } from 'configuration/notifications';
import { agreementApi } from 'helpers/api/agreement';
import { getUnixTimestamp, getUUID } from 'helpers/utils';

import { rentSelector, accrualListSelector } from 'store/transferAct/selectors';
import { stockSelector, stockListSelector } from 'store/stock/selectors';
import { Client } from 'store/client/actions';
import { Stock } from 'store/stock/actions';
import { Rent } from 'store/transferAct/actions';
import { agreementsListSelector, organizationsListSelector } from 'store/client/selectors';

import { ActionButton } from 'components/ActionButton';
import { ModalClient } from 'components/ModalClient';
import { ModalAttention } from 'components/ModalAttention';
import { ModalInstruments } from 'components/ModalInstruments';
import { InputAutoComplete } from 'components/InputAutoComplete/InputAutoComplete';

const ActionButtonsSection = () => {
  const dispatch = useDispatch();

  const rent = useSelector(rentSelector);
  const stock = useSelector(stockSelector);
  const stockList = useSelector(stockListSelector);
  const accrualSubTypes = useSelector(accrualListSelector);
  const clientAgreements = useSelector(agreementsListSelector);
  const organizationList = useSelector(organizationsListSelector);

  const [client, setClient] = useState({ open: false, key: 'client', data: {} });
  const [instruments, setInstruments] = useState({ open: false, key: 'instruments', data: [] });
  const [instrumentInModal, setInstrumentInModal] = useState(null);
  const [comment, setComment] = useState({ open: false, key: 'comment', data: '' });
  const [address, setAddress] = useState({ open: false, key: 'address', data: '' });
  const [bonus, setBonus] = useState({ open: false, key: 'bonusSpent', data: 0 });
  const [agr, setAgr] = useState({ open: false, key: 'agr', data: { value: null, selected: null } });
  const [fine, setFine] = useState({ open: false, key: 'fine', data: { selector: null, price: 0, comment: '' } });
  const [discount, setDiscount] = useState({
    open: false,
    key: 'discount',
    data: { selector: null, price: 0, comment: '' },
  });

  useEffect(() => {
    dispatch(Rent.getAccrualList());
    dispatch(Client.getOrganizations());
  }, []);

  useEffect(() => {
    if (clientAgreements?.length === 1 && !agr?.data?.value) {
      setAgr({ ...agr, data: { value: clientAgreements[0], selected: null } });
      handleChange({ ...agr, data: clientAgreements[0] });
    }
  }, [clientAgreements]);

  useEffect(() => {
    if (rent) {
      setClient({ ...client, data: rent?.client });
      setInstruments({ ...instruments, data: rent?.instruments });
      setComment({ ...comment, data: rent?.comment || '' });
      setAddress({ ...address, data: rent?.address || '' });
      setBonus({ ...bonus, data: rent?.bonusSpent || 0 });

      setAgr({
        ...agr,
        data: {
          value: rent?.agr || null,
        },
      });
    }
  }, [rent]);

  const handleCloseButton = () => {
    setComment({ ...comment, open: false });
    setAddress({ ...address, open: false });
    setBonus({ ...bonus, open: false });
    setAgr({ ...agr, open: false });
    setFine((state) => ({ ...state, open: false }));
    setDiscount((state) => ({ ...state, open: false }));
  };

  const addInstrumentValue = (value) => {
    if (instruments.data.find((elem) => elem.id == value.id)) {
      return;
    }
    if (value?.quantity_inreserve_service > 0) {
      setInstrumentInModal(value);
      return;
    }
    handleChange({ key: 'instruments', data: value });
  };

  const handleSetAgr = () => {
    const { value, selected } = agr.data;
    if (value) {
      handleChange({ ...agr, data: value });
    } else if (selected) {
      if (!rent.client) {
        errorToast('Сначала выберите клиента!');
        return;
      }

      agreementApi
        .createAgreement({
          clientID: rent.client.id,
          orgID: selected.orgID1C,
          date: getUnixTimestamp(),
          term: getUnixTimestamp() + 3600 * 24 * 365 * 2,
          type: '',
        })
        .then(({ data } = {}) => {
          handleChange({ ...agr, data });
        });
    }
  };

  const handleChange = (target) => {
    const newRent = {};

    switch (target.key) {
      case 'client':
        if (target.data && target.data.isTemporary) {
          infoToast('Клиент не полностью заполнен. Отправка данных в 1C невозможна');
        }

        newRent.client = target.data;
        newRent.agr = null;
        dispatch(Client.getAgreements(target.data.id));
        newRent.bonusSpent = 0;
        break;

      case 'agr':
        newRent.agr = target.data;
        break;

      case 'bonusSpent':
        newRent.bonusSpent = target.data;
        break;

      case 'comment':
        newRent.comment = target.data;
        break;

      case 'address':
        newRent.address = target.data;
        break;

      case 'fine':
        newRent.fine = [
          ...rent.fine,
          {
            sum: target.data.price,
            comment: target.data.comment,
            accrualSubType: target.data.selector,
            fronEndID: getUUID(),
          },
        ];
        setFine({
          open: false,
          key: 'fine',
          data: { selector: null, price: 0, comment: '' },
        });
        break;

      case 'discount':
        newRent.discount = [
          ...rent.discount,
          {
            sum: target.data.price,
            comment: target.data.comment,
            accrualSubType: target.data.selector,
            fronEndID: getUUID(),
          },
        ];
        setDiscount({
          open: false,
          key: 'discount',
          data: { selector: null, price: 0, comment: '' },
        });
        break;

      case 'instruments':
        if (!rent.instruments.find((inst) => inst.id === target.data.id)) {
          if (target.data.stockID !== stock.stockID1C && target.data.stockID !== '') {
            if (rent.instruments.length === 0 || stock.stockName === 'Доставка') {
              const newStock = stockList.find((stock) => stock.stockID1C === target.data.stockID);

              if (newStock) {
                dispatch(Stock.setStock(newStock));
                newRent.stockID = newStock.stockID1C;
              }
            } else {
              errorToast('Нельзя брать инструменты из разных складов!');
              return;
            }
          }

          const newChosenItem = {
            ...target.data,
            quantity: 1,
            rentTerm: 1,
          };

          if (newChosenItem.is_priced_by_wear) {
            newChosenItem.quantity = 0;
            newChosenItem.rentTerm = 0;
          }

          newRent.instruments = [...rent.instruments, { ...newChosenItem }];
        }
        break;

      default:
        return;
    }

    handleCloseButton();
    dispatch(Rent.updateRent(newRent));
  };

  const onCloseModal = () => {
    setInstrumentInModal(null);
  };

  const onSubmitModal = () => {
    handleChange({ key: 'instruments', data: instrumentInModal });
    setInstrumentInModal(null);
  };

  const setClientValueHandled = (value) => {
    const newClient = { ...client, open: false, data: value };
    setAgr({ open: false, data: { value: null, selected: null } });
    setClient(newClient);
    handleChange(newClient);
  };

  const handleValidateAccrual = (accrual) => {
    if (!accrual.data.selector?.id) {
      errorToast('Нужно выбрать обоснование!');
      return;
    }

    if (accrual.data.price <= 0) {
      errorToast('Неверная сумма!');
      return;
    }

    handleChange(accrual);
  };

  const setBonusValue = (value) => {
    setBonus({ ...bonus, data: value });
  };

  const setCommentValue = (value) => {
    setComment({ ...comment, data: value });
  };

  const setAddressValue = (value) => {
    setAddress({ ...address, data: value });
  };

  const setDiscountValue = (target, value) => {
    setDiscount({ ...discount, data: { ...discount.data, [target]: value } });
  };

  const setFineValue = (target, value) => {
    setFine({ ...fine, data: { ...fine.data, [target]: value } });
  };

  const getBonusMax = useMemo(() => {
    const rateVAT = rent?.agr?.organization?.rateVAT || 0;

    if (!rent?.instruments) {
      return 0;
    }

    const clientMax = rent?.client?.bonusAccount || 0;

    const instrumentsMax = rent.instruments.reduce((acc, cur) => {
      return acc + (cur.itemRentSum * cur.quantity + ((cur.itemRentSum * cur.quantity) / 100) * rateVAT) / 2;
    }, 0);

    if (instrumentsMax > clientMax) {
      return clientMax;
    }

    return instrumentsMax;
  }, [rent?.instruments, rent?.agr, rent?.client]);

  return (
    <div className="rent-section">
      <ActionButton text="Контрагент" onClick={() => setClient({ ...client, open: true })} icon="user-btn" />
      {client.open && (
        <ModalClient
          historyParams={{ fromRent: `/rent${rent.actID1C ? `/${rent.actID1C}` : ''}` }}
          selected={client.data}
          onSubmit={setClientValueHandled}
          onClose={() => setClient({ ...client, open: false })}
        />
      )}

      <ActionButton
        text="Номенклатура"
        onClick={() => setInstruments({ ...instruments, open: true })}
        icon="instrument-btn"
      />
      {instruments.open && (
        <ModalInstruments
          selected={instruments.data}
          defaultStock={stock || null}
          onAdd={addInstrumentValue}
          onClose={() => setInstruments({ ...instruments, open: false })}
        />
      )}

      <ActionButton
        text="Скидка"
        closeText="Добавить"
        size="-90px"
        onSubmit={(e) => handleValidateAccrual(discount)}
        isOpen={discount.open}
        onClick={(open) => setDiscount({ ...discount, open })}
        icon="discount-btn"
      >
        <div className="modal-body">
          <InputAutoComplete
            icon="search"
            onChange={(selector) => setDiscountValue('selector', selector)}
            list={accrualSubTypes
              .filter((subtype) => subtype.category.includes('Знижки'))
              .map((subtype) => ({
                value: subtype.name,
                text: subtype.name,
                returning: subtype,
              }))}
            selected={
              discount.data.selector ? { text: discount.data.selector.name, value: discount.data.selector.name } : null
            }
            placeholder="Обоснование"
            type="text"
            size="none"
          />

          <div className="modal-body-date margin-t10 flex-center">
            <InputButton
              value={discount.data.price || 0}
              onChange={(price) => setDiscountValue('price', price)}
              className="input-property"
              type="number"
              style={{ marginRight: '5px', flex: 1 }}
            />
            грн.
            <button
              onClick={() => setDiscountValue('price', '50')}
              className="btn-property"
              style={{ margin: '0 3px' }}
            >
              50
            </button>
            <button
              onClick={() => setDiscountValue('price', '100')}
              className="btn-property"
              style={{ margin: '0 3px' }}
            >
              100
            </button>
          </div>
          <TextAreaButton
            value={discount.data.comment || ''}
            onChange={(comment) => setDiscountValue('comment', comment)}
            placeholder="Комментарий..."
          />
        </div>
      </ActionButton>

      <ActionButton
        text="Штраф"
        closeText="Добавить"
        size="-90px"
        onSubmit={(e) => handleValidateAccrual(fine)}
        isOpen={fine.open}
        onClick={(open) => setFine({ ...fine, open })}
        icon="fine-btn"
      >
        <div className="modal-body">
          <InputAutoComplete
            icon="search"
            onChange={(selector) => setFineValue('selector', selector)}
            list={accrualSubTypes
              .filter((subtype) => subtype.category.includes('Додаткові нарахування'))
              .map((subtype) => ({
                value: subtype.name,
                text: subtype.name,
                returning: subtype,
              }))}
            selected={fine.data.selector ? { text: fine.data.selector.name, value: fine.data.selector.name } : null}
            placeholder="Обоснование"
            type="text"
            size="none"
          />

          <div className="modal-body-date margin-t10 flex-center">
            <InputButton
              value={fine.data.price || 0}
              onChange={(price) => setFineValue('price', price)}
              className="input-property"
              type="number"
              style={{ marginRight: '5px', flex: 1 }}
            />
            грн.
            <button onClick={() => setFineValue('price', '50')} className="btn-property" style={{ margin: '0 3px' }}>
              50
            </button>
            <button onClick={() => setFineValue('price', '100')} className="btn-property" style={{ margin: '0 3px' }}>
              100
            </button>
          </div>
          <TextAreaButton
            value={fine.data.comment || ''}
            onChange={(comment) => setFineValue('comment', comment)}
            placeholder="Комментарий..."
          />
        </div>
      </ActionButton>
      {instrumentInModal && (
        <ModalAttention
          headerText=""
          className="attention_instrument"
          onClose={onCloseModal}
          onSubmit={onSubmitModal}
          bodyText={
            <>
              Товар <b>{instrumentInModal?.title}</b> в кол-ве{' '}
              <b>{instrumentInModal?.quantity_inreserve_service} шт </b>
              находится в резерве. Вы уверены, что хотите выбрать его повторно?
            </>
          }
          submitTextButton="Да, выбрать"
          cancelTextButton="Нет, отмена"
        />
      )}
      {rent?.client && (
        <>
          <ActionButton
            text="Договор"
            closeText="Выбрать"
            size="-190px"
            onSubmit={(e) => handleSetAgr()}
            isOpen={agr.open}
            onClick={(open) => setAgr({ ...agr, open })}
            icon="fop-btn"
          >
            <>
              <div className="flex-center">
                <b>Выберите один из существующих договоров с ФОП:</b>
              </div>
              <div className="select-list dropdownScroll">
                {clientAgreements &&
                  clientAgreements.map((agreement, i) => {
                    const { value } = agr.data;
                    if (agreement.term < getUnixTimestamp()) {
                      return <></>;
                    }

                    const isActive = value?.id === agreement.id;

                    return (
                      <div
                        className={`select-prop${isActive ? ' is-selected' : ''}`}
                        key={agreement.id}
                        onClick={() => {
                          setAgr({ ...agr, data: { value: agreement, selected: null } });
                        }}
                      >
                        Договор с {agreement.organization.orgName} от{' '}
                        {moment(agreement.date * 1000).format('DD.MM.YYYY')}
                      </div>
                    );
                  })}
              </div>
              <div className="flex-center" style={{ marginBottom: '5px' }}>
                <b>Или создайте новый договор с:</b>
              </div>
              <div style={{ width: '328px', margin: '0 auto' }}>
                <InputAutoComplete
                  icon="search"
                  onChange={(selected) => {
                    setAgr({ ...agr, data: { selected, value: null } });
                  }}
                  list={organizationList.map((org) => ({
                    value: org.orgID1C,
                    text: org.orgName,
                    returning: org,
                  }))}
                  selected={
                    agr.data.selected ? { text: agr.data.selected.orgName, value: agr.data.selected.orgID1C } : null
                  }
                  placeholder="ФОП..."
                  type="text"
                  size="none"
                />
              </div>
            </>
          </ActionButton>

          <ActionButton
            text="Списать бонус"
            closeText="Списать"
            size="-50px"
            onSubmit={() => handleChange(bonus)}
            isOpen={bonus.open}
            onClick={(open) => setBonus({ ...bonus, open })}
            color="button-gold"
            icon="bonus-btn"
          >
            <div className="modal-body">
              <div className="modal-body-date">
                <InputButton
                  value={bonus.data || 0}
                  onChange={(value) => {
                    if (value > getBonusMax) {
                      return;
                    }
                    setBonusValue(value);
                  }}
                  className="input-property"
                  style={{ marginRight: '5px', flex: 1 }}
                  type="number"
                />
                грн.
                <button
                  type="button"
                  onClick={(e) => setBonusValue(getBonusMax)}
                  className="btn-property"
                  style={{ margin: '0 3px' }}
                >
                  Максимум
                </button>
              </div>
            </div>
          </ActionButton>
        </>
      )}
      <ActionButton
        text="Комментарий"
        closeText="Добавить"
        size="-70px"
        onSubmit={(e) => handleChange(comment)}
        isOpen={comment.open}
        onClick={(open) => setComment({ ...comment, open })}
        icon="comment-btn"
      >
        <div className="modal-body">
          <TextAreaButton
            value={comment.data || ''}
            onChange={(value) => setCommentValue(value)}
            placeholder="Комментарий..."
          />
        </div>
      </ActionButton>

      <ActionButton
        text="Адрес эксплуатации"
        closeText="Добавить"
        size="-50px"
        onSubmit={(e) => handleChange(address)}
        isOpen={address.open}
        onClick={(open) => setAddress({ ...address, open })}
        icon="address-btn"
      >
        <div className="modal-body">
          <TextAreaButton
            value={address.data || ''}
            onChange={(value) => setAddressValue(value)}
            placeholder="Введите адрес эксплуатации (доставки)..."
          />
        </div>
      </ActionButton>
    </div>
  );
};

const TextAreaButton = ({ onChange, ...props }) => {
  return (
    <textarea
      type="text"
      className="modal-textarea"
      {...props}
      onChange={({ target: { value } }) => onChange(value)}
    ></textarea>
  );
};

TextAreaButton.propTypes = {
  onChange: PropTypes.func,
};

const InputButton = ({ onChange, ...props }) => {
  return <input {...props} onChange={({ target: { value } }) => onChange(value)}></input>;
};

InputButton.propTypes = {
  onChange: PropTypes.func,
};

export { ActionButtonsSection };
